import { useMemo } from "react";
import { scaleLinear, scaleTime } from "@visx/scale";
import { addDays, subDays } from "date-fns/esm";

import { WorkerUsageData } from "../types";
import { DAY_MS } from "../constants";
import { getWorkerUsageYAxisTarget, hasWorkerUsageContract } from "../utils";
import {
  AXIS_CYCLE_SEGMENTS,
  CONCURRENCY_CHART_MARGIN,
  DAY_BAND_WIDTH_RATIO,
  DOMAIN_PADDING_LEFT_DAYS,
  DOMAIN_PADDING_RIGHT_DAYS,
  MARKER_LABEL,
  MARKER_LABEL_GAP,
  MARKER_LABEL_ROW_HEIGHT,
  MARKER_LABEL_Y,
  MIN_DAY_BAND_WIDTH,
  MIN_PLOT_WIDTH,
  REFERENCE_BADGE_CHAR_WIDTH,
  X_RANGE_PADDING,
  Y_AXIS_NUM_TICKS,
} from "./constants";

// Labels are centred on their markers, so two of them collide once their centres
// are closer than half of both texts plus the gap kept between them.
const labelsCollide = (a: string, aX: number, b: string, bX: number) =>
  Math.abs(aX - bX) < ((a.length + b.length) * REFERENCE_BADGE_CHAR_WIDTH) / 2 + MARKER_LABEL_GAP;

// Turns the usage data and the measured viewport into everything the plot
// needs: scales, dimensions, axis ticks and the recorded days clipped to the
// domain.
const useConcurrencyChartLayout = (
  usage: WorkerUsageData,
  viewportWidth: number,
  height: number
) => {
  const domainStart = useMemo(
    () => subDays(usage.cycleStart, DOMAIN_PADDING_LEFT_DAYS),
    [usage.cycleStart]
  );
  const domainEnd = useMemo(
    () => addDays(usage.cycleEnd, DOMAIN_PADDING_RIGHT_DAYS),
    [usage.cycleEnd]
  );

  const totalDomainDays = Math.max(
    Math.round((domainEnd.getTime() - domainStart.getTime()) / DAY_MS),
    1
  );

  const plotWidth = Math.max(
    viewportWidth - CONCURRENCY_CHART_MARGIN.left - CONCURRENCY_CHART_MARGIN.right,
    0
  );

  const effectivePlotWidth = Math.max(plotWidth, MIN_PLOT_WIDTH);
  const pixelsPerDay = effectivePlotWidth > 0 ? effectivePlotWidth / totalDomainDays : 0;

  const xMax = pixelsPerDay * totalDomainDays;
  const effectiveWidth = xMax + CONCURRENCY_CHART_MARGIN.left + CONCURRENCY_CHART_MARGIN.right;

  const dayBandWidth = Math.max(pixelsPerDay * DAY_BAND_WIDTH_RATIO, MIN_DAY_BAND_WIDTH);

  // Inset by half a bar so the first and last day sit fully inside the plot.
  const xRangePadding = X_RANGE_PADDING + dayBandWidth / 2;
  const xScale = useMemo(
    () =>
      scaleTime({
        domain: [domainStart, domainEnd],
        range: [xRangePadding, Math.max(xMax - xRangePadding, xRangePadding)],
      }),
    [domainStart, domainEnd, xMax, xRangePadding]
  );

  const todayX = xScale(usage.today);
  const cycleStartX = xScale(usage.cycleStart);
  const cycleEndX = xScale(usage.cycleEnd);

  // Cycle start and end sit a whole cycle apart, so only "Today" can run into one:
  // on the first and last days, plus a few around them on a narrow plot. Its label
  // then rides a row above, and the header band grows by that row.
  const isTodayLabelStacked =
    hasWorkerUsageContract(usage) &&
    (labelsCollide(MARKER_LABEL.today, todayX, MARKER_LABEL.cycleStart, cycleStartX) ||
      labelsCollide(MARKER_LABEL.today, todayX, MARKER_LABEL.cycleEnd, cycleEndX));

  const marginTop =
    CONCURRENCY_CHART_MARGIN.top + (isTodayLabelStacked ? MARKER_LABEL_ROW_HEIGHT : 0);
  const yMax = height - marginTop - CONCURRENCY_CHART_MARGIN.bottom;

  // Phase-aligned to the cycle start so its start and end always land on a tick,
  // with the same cadence tiled across the runway.
  const axisTickValues = useMemo(() => {
    const anchor = usage.cycleStart.getTime();
    const stepMs = (usage.cycleEnd.getTime() - anchor) / AXIS_CYCLE_SEGMENTS;
    if (stepMs <= 0) return [usage.cycleStart];

    const startMs = domainStart.getTime();
    const endMs = domainEnd.getTime();
    const firstIndex = Math.ceil((startMs - anchor) / stepMs);
    const lastIndex = Math.floor((endMs - anchor) / stepMs);

    const ticks: Date[] = [];
    for (let index = firstIndex; index <= lastIndex; index += 1) {
      ticks.push(new Date(anchor + index * stepMs));
    }
    return ticks;
  }, [usage.cycleStart, usage.cycleEnd, domainStart, domainEnd]);

  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [0, getWorkerUsageYAxisTarget(usage)],
        range: [yMax, 0],
        nice: Y_AXIS_NUM_TICKS,
      }),
    [usage, yMax]
  );

  const yAxisCap = yScale.domain()[1];

  // The API returns one point per elapsed day, so this only clips to the domain.
  const recordedDays = useMemo(
    () =>
      usage.daily.filter(
        (point) =>
          point.day.getTime() >= domainStart.getTime() && point.day.getTime() <= domainEnd.getTime()
      ),
    [usage.daily, domainStart, domainEnd]
  );

  return {
    xScale,
    yScale,
    xMax,
    yMax,
    marginTop,
    todayLabelY: isTodayLabelStacked ? MARKER_LABEL_Y - MARKER_LABEL_ROW_HEIGHT : MARKER_LABEL_Y,
    effectiveWidth,
    dayBandWidth,
    axisTickValues,
    yAxisCap,
    recordedDays,
    todayX,
    cycleStartX,
    cycleEndX,
  };
};

export default useConcurrencyChartLayout;
